import { Component, Inject, OnInit } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialogModule,
} from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import {
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { UserService, User } from "../../../core/services/user.service";
import { SiteService, Site } from "../../../core/services/site.service";
import { GroupService, Group } from "../../../core/services/group.service";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AuthService } from "../../../core/services/auth.service";
import { UserTypeService } from "../../../core/services/user-type.service";
import { UpdateUserRoleResponse } from "../../../core/services/user.service";
import { switchMap, map } from "rxjs/operators";

type UserRole = "admin" | "super_admin" | "hr_manager" | "hr_personnel" | "consultant" | "default";

@Component({
    selector: "app-view-user-dialog",
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatSnackBarModule,
        MatTooltipModule,
    ],
    template: `
        <div class="dialog-header">
            <h2 mat-dialog-title>{{ isEditMode ? 'Edit User' : 'View User' }}</h2>
            <p class="dialog-subtitle">
                {{ isEditMode ? 'Update user information' : 'User information' }}
            </p>
        </div>

        <div *ngIf="!isEditMode" class="view-mode">
            <mat-dialog-content class="dialog-content">
                <div class="info-section">
                    <div class="info-row">
                        <div class="info-label">First Name</div>
                        <div class="info-value">{{ data.user.name }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Last Name</div>
                        <div class="info-value">{{ data.user.surname }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Email Address</div>
                        <div class="info-value">{{ data.user.email }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Cellphone</div>
                        <div class="info-value">{{ formatPhoneNumber(data.user.msisdn) }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">ID/Passport Number</div>
                        <div class="info-value">{{ data.user.identity_number || 'N/A' }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Gender</div>
                        <div class="info-value">{{ data.user.gender || 'N/A' }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Job Description</div>
                        <div class="info-value">{{ data.user.job_description || 'N/A' }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Site</div>
                        <div class="info-value">{{ getSiteName(data.user.site_id) }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Group</div>
                        <div class="info-value">{{ getGroupName(data.user.group_id) }}</div>
                    </div>
                    <div class="info-row">
                        <div class="info-label">Role</div>
                        <div class="info-value">{{ mapsLoaded ? getRoleLabel(data.user.user_type_id) : 'Loading…' }}</div>
                    </div>
                    <div *ngIf="mapsLoaded && authService.canAddUsers() && isCandidate()" class="invite-hint">
                        <mat-icon class="hint-icon">info_outline</mat-icon>
                        <span>To upgrade this user's role, use the </span>
                        <button mat-button color="primary" class="hint-link-btn" (click)="openInviteForUser()">Invite flow</button>
                    </div>
                    <div *ngIf="mapsLoaded && authService.canAddUsers() && !isCandidate()" class="invite-hint">
                        <mat-icon class="hint-icon">mail_outline</mat-icon>
                        <span>Staff member can't log in? </span>
                        <button mat-button color="primary" class="hint-link-btn"
                            [disabled]="resendingSetup"
                            (click)="resendAccountSetup()">
                            {{ resendingSetup ? 'Sending…' : 'Resend account setup' }}
                        </button>
                    </div>
                </div>
            </mat-dialog-content>
            <mat-dialog-actions align="end" class="dialog-actions">
                <button mat-button mat-dialog-close type="button" class="close-btn">
                    Close
                </button>
                @if (mapsLoaded && (authService.isAdminOrManager() || (authService.canAddUsers() && !isManagerLevelUser(data.user.user_type_id)))) {
                <button
                    mat-raised-button
                    color="primary"
                    type="button"
                    (click)="enableEditMode()"
                    class="edit-btn"
                    data-tour-id="user-profile-edit-btn"
                >
                    Edit
                </button>
                }
            </mat-dialog-actions>
        </div>

        <form *ngIf="isEditMode" [formGroup]="form" (ngSubmit)="onSubmit()">
            <mat-dialog-content class="dialog-content">
                <div class="form-row">
                    <mat-form-field appearance="outline" class="form-field">
                        <mat-label>First Name</mat-label>
                        <input matInput formControlName="name" placeholder="First name" />
                        <mat-error *ngIf="form.get('name')?.hasError('required')">
                            First name is required
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="form-field">
                        <mat-label>Last Name</mat-label>
                        <input matInput formControlName="surname" placeholder="Last name" />
                        <mat-error *ngIf="form.get('surname')?.hasError('required')">
                            Last name is required
                        </mat-error>
                    </mat-form-field>
                </div>

                <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Email Address</mat-label>
                    <input
                        matInput
                        type="email"
                        formControlName="email"
                        placeholder="Email address"
                    />
                    <mat-error *ngIf="form.get('email')?.hasError('email')">
                        Please enter a valid email address
                    </mat-error>
                </mat-form-field>

                <div class="phone-container">
                    <div class="select-container country-select">
                         <div class="select-wrapper">
                             <select formControlName="countryCode" class="custom-select">
                                 <option *ngFor="let country of countries" [value]="country.code">
                                     {{ country.name }} ({{ country.dial_code }})
                                 </option>
                             </select>
                             <div class="select-arrow">
                                 <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                     <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                 </svg>
                             </div>
                         </div>
                    </div>

                    <mat-form-field appearance="outline" class="phone-input">
                        <mat-label>Cellphone</mat-label>
                        <input
                            matInput
                            type="text"
                            formControlName="msisdn"
                            placeholder="Enter number"
                            inputmode="numeric"
                            (keypress)="onNumberKeyPress($event)"
                            (input)="onNumberInput($event, 'msisdn')"
                        />
                        <mat-error *ngIf="form.get('msisdn')?.hasError('required')">
                            Cellphone is required
                        </mat-error>
                        <mat-error *ngIf="form.get('msisdn')?.hasError('pattern')">
                            Invalid number format
                        </mat-error>
                    </mat-form-field>
                </div>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>ID/Passport Number</mat-label>
                    <input
                        matInput
                        type="text"
                        formControlName="identity_number"
                        placeholder="ID or Passport Number"
                        maxlength="50"
                        inputmode="numeric"
                        (keypress)="onNumberKeyPress($event)"
                        (input)="onNumberInput($event, 'identity_number')"
                    />
                    <mat-error *ngIf="form.get('identity_number')?.hasError('required')">
                        ID/Passport number is required
                    </mat-error>
                    <mat-error *ngIf="form.get('identity_number')?.hasError('minlength')">
                        Must be at least 6 characters
                    </mat-error>
                    <mat-error *ngIf="form.get('identity_number')?.hasError('pattern')">
                        Only numbers are allowed
                    </mat-error>
                    <small class="help-text">
                        Enter your ID number or passport number (minimum 6 digits, numbers only)
                    </small>
                </mat-form-field>

                <div class="form-row">
                    <mat-form-field appearance="outline" class="form-field">
                        <mat-label>Gender</mat-label>
                        <mat-select id="genderSelect" formControlName="gender">
                            <mat-option value="">Select Gender</mat-option>
                            <mat-option value="Male">Male</mat-option>
                            <mat-option value="Female">Female</mat-option>
                            <mat-option value="Other">Other</mat-option>
                        </mat-select>
                        <mat-error *ngIf="form.get('gender')?.hasError('required')">
                            Gender is required
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="form-field">
                        <mat-label>Job Description</mat-label>
                        <input
                            matInput
                            formControlName="job_description"
                            placeholder="Enter job description"
                        />
                    </mat-form-field>
                </div>

                <div class="form-row">
                    <div class="select-container">
                        <label class="select-label">Site</label>
                        <div class="select-wrapper">
                            <select formControlName="site_id" class="custom-select"
                                [disabled]="sitesLoading">
                                <option [ngValue]="null">— Select Site —</option>
                                @for (s of sites; track s.id) {
                                    <option [ngValue]="s.id">{{ s.name }}</option>
                                }
                            </select>
                            <div class="select-arrow">
                                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div class="select-container">
                        <label class="select-label">Group (optional)</label>
                        <div class="select-wrapper">
                            <select formControlName="group_id" class="custom-select" [disabled]="groupsLoading">
                                <option [ngValue]="null">— No Group —</option>
                                @for (g of groups; track g.id) {
                                    <option [ngValue]="g.id">{{ g.name }}</option>
                                }
                            </select>
                            <div class="select-arrow">
                                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                    <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <mat-form-field *ngIf="canEditRole" appearance="outline" class="full-width">
                    <mat-label>Role</mat-label>
                    <mat-select formControlName="user_type">
                        <mat-option *ngFor="let r of roleOptions" [value]="r.value">{{ r.label }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </mat-dialog-content>
            <mat-dialog-actions align="end" class="dialog-actions">
                <button mat-button type="button" (click)="cancelEdit()" class="cancel-btn">
                    Cancel
                </button>
                <button
                    mat-raised-button
                    color="primary"
                    type="submit"
                    [disabled]="form.invalid"
                    class="save-btn"
                >
                    Save Changes
                </button>
            </mat-dialog-actions>
        </form>
    `,
    styles: [
        `
            .dialog-header {
                padding: 24px 24px 16px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                margin-bottom: 0;
                background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
                border-radius: 4px 4px 0 0;
            }

            .dialog-header h2 {
                margin: 0 0 8px 0;
                font-size: 20px;
                font-weight: 500;
                color: #ffffff;
            }

            .dialog-subtitle {
                margin: 0;
                color: rgba(255, 255, 255, 0.9);
                font-size: 14px;
                font-weight: 400;
            }

            .dialog-content {
                padding: 24px;
                min-width: 500px;
            }

            .view-mode .info-section {
                display: flex;
                flex-direction: column;
                gap: 20px;
            }

            .info-row {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .info-label {
                font-size: 12px;
                font-weight: 500;
                color: #666;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .info-value {
                font-size: 16px;
                color: #333;
                font-weight: 400;
            }

            .form-row {
                display: flex;
                gap: 16px;
                margin-bottom: 8px;
            }

            .form-field {
                flex: 1;
                margin-bottom: 16px;
            }

            .full-width {
                width: 100%;
                margin-bottom: 16px;
            }

            .phone-container {
                display: flex;
                flex-direction: row;
                gap: 16px;
                align-items: flex-start;
                margin-bottom: 16px;
                width: 100%;
            }

            .select-container.country-select {
                width: 130px;
                flex: 0 0 130px;
                margin-bottom: 0;
            }

            .phone-input {
                flex: 1;
                width: auto;
            }

            .dialog-actions {
                padding: 16px 24px 24px;
                border-top: 1px solid #e0e0e0;
                margin-top: 8px;
                gap: 12px;
            }

            .close-btn,
            .cancel-btn {
                color: #666;
                font-weight: 500;
                padding: 8px 20px;
                border-radius: 8px;
                transition: all 0.2s ease;
            }

            .close-btn:hover,
            .cancel-btn:hover {
                background-color: #f5f5f5;
                color: #333;
            }

            .edit-btn,
            .save-btn {
                background: linear-gradient(
                    135deg,
                    #1e3a8a 0%,
                    #3b82f6 100%
                );
                color: white;
                font-weight: 500;
                padding: 8px 24px;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(30, 58, 138, 0.3);
                transition: all 0.2s ease;
            }

            .edit-btn:hover:not(:disabled),
            .save-btn:hover:not(:disabled) {
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(30, 58, 138, 0.4);
            }

            .save-btn:disabled {
                opacity: 0.6;
                transform: none;
                box-shadow: none;
            }

            /* Material form field customizations */
            :host ::ng-deep .mat-mdc-form-field {
                .mdc-notched-outline__leading,
                .mdc-notched-outline__notch,
                .mdc-notched-outline__trailing {
                    border-color: #e0e0e0;
                }

                &.mat-focused {
                    .mdc-notched-outline__leading,
                    .mdc-notched-outline__notch,
                    .mdc-notched-outline__trailing {
                        border-color: #1e3a8a;
                        border-width: 2px;
                    }

                    .mdc-floating-label {
                        color: #1e3a8a;
                    }
                }

                .mdc-text-field--filled {
                    background-color: #fafafa;
                }
            }

            /* Custom Select Styles */
            .select-container {
                width: 100%;
                margin-bottom: 16px;
            }

            .form-row .select-container {
                display: flex;
                flex-direction: column;
                flex: 1;
                margin-bottom: 16px;
            }

            .select-label {
                display: block;
                margin-bottom: 4px;
                font-size: 12px;
                font-weight: 400;
                color: rgba(0, 0, 0, 0.6);
                font-family: "Roboto", sans-serif;
                transform: translateY(0);
                transition: color 0.2s ease;
            }

            .form-row .select-container .select-label {
                margin-bottom: 8px;
                font-size: 14px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
            }

            .select-wrapper {
                position: relative;
                width: 100%;
            }

            .custom-select {
                width: 100%;
                height: 56px;
                padding: 16px 48px 16px 16px;
                border: 1px solid rgba(0, 0, 0, 0.23);
                border-radius: 4px;
                font-size: 16px;
                font-family: "Roboto", sans-serif;
                background: #fff;
                color: rgba(0, 0, 0, 0.87);
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                cursor: pointer;
                transition: all 0.2s ease-in-out;
                outline: none;
            }

            .custom-select:hover {
                border-color: rgba(0, 0, 0, 0.87);
            }

            .custom-select:focus {
                border-color: #1e3a8a;
                border-width: 2px;
                box-shadow: 0 0 0 1px rgba(30, 58, 138, 0.2);
            }

            .custom-select:disabled {
                background-color: rgba(0, 0, 0, 0.04);
                color: rgba(0, 0, 0, 0.38);
                cursor: not-allowed;
            }

            .select-arrow {
                position: absolute;
                right: 16px;
                top: 50%;
                transform: translateY(-50%);
                color: rgba(0, 0, 0, 0.54);
                transition: transform 0.2s ease-in-out;
            }

            .custom-select:focus + .select-arrow {
                color: #1e3a8a;
            }

            .custom-select option {
                padding: 12px 16px;
                font-size: 16px;
                color: rgba(0, 0, 0, 0.87);
                background: #fff;
            }

            .custom-select option:hover {
                background-color: rgba(0, 0, 0, 0.04);
            }

            .error-message {
                font-size: 12px;
                color: #f44336;
                margin-top: 4px;
                font-family: "Roboto", sans-serif;
            }

            .help-text {
                display: block;
                font-size: 12px;
                color: #666;
                margin-top: 4px;
                font-family: "Roboto", sans-serif;
                line-height: 1.3;
            }

            /* Responsive design */
            @media (max-width: 600px) {
                .dialog-content {
                    min-width: auto;
                    padding: 16px;
                }

                .form-row {
                    flex-direction: column;
                    gap: 0;
                }

                .dialog-header {
                    padding: 16px 16px 12px;
                }

                .dialog-actions {
                    padding: 12px 16px 16px;
                    flex-direction: column-reverse;
                }

                .close-btn,
                .cancel-btn,
                .edit-btn,
                .save-btn {
                    width: 100%;
                    margin: 4px 0;
                }
            }

            /* ── Dark mode ─────────────────────────────────────────────── */
            :host-context(.dark) .dialog-header {
                background: linear-gradient(135deg, #1a2d5a 0%, #1e3a7a 100%);
            }

            :host-context(.dark) .info-label {
                color: #8b949e;
            }

            :host-context(.dark) .info-value {
                color: #e6edf3;
            }

            :host-context(.dark) .dialog-actions {
                border-top-color: #21262d;
            }

            :host-context(.dark) .close-btn,
            :host-context(.dark) .cancel-btn {
                color: #e6edf3;
                background: rgba(255, 255, 255, 0.1);
            }

            :host-context(.dark) .close-btn:hover,
            :host-context(.dark) .cancel-btn:hover {
                background-color: rgba(255, 255, 255, 0.16);
                color: #e6edf3;
            }

            :host-context(.dark) .select-label {
                color: #8b949e;
            }

            :host-context(.dark) .custom-select {
                background: #1e2843;
                color: #e6edf3;
                border-color: #3d4f6e;
            }

            :host-context(.dark) .custom-select:hover {
                border-color: #6b8cba;
            }

            :host-context(.dark) .custom-select:focus {
                border-color: #3b82f6;
                box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
            }

            :host-context(.dark) .custom-select option {
                background: #1e2843;
                color: #e6edf3;
            }

            :host-context(.dark) .select-arrow {
                color: #8b949e;
            }

            :host-context(.dark) .form-row .select-container .select-label {
                color: #e6edf3;
            }

            :host-context(.dark) .help-text {
                color: #8b949e;
            }

            .invite-hint {
                display: flex;
                align-items: center;
                gap: 4px;
                margin-top: 12px;
                padding: 10px 14px;
                background: #e3f2fd;
                border-radius: 8px;
                font-size: 14px;
                color: #1565c0;
                flex-wrap: wrap;
            }

            .hint-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: #1976d2;
            }

            .hint-link-btn {
                padding: 0 4px;
                min-width: unset;
                line-height: 1.4;
                font-weight: 600;
            }

            :host-context(.dark) .invite-hint {
                background: rgba(59, 130, 246, 0.12);
                color: #93c5fd;
            }
        `,
    ],
})
export class ViewUserDialogComponent implements OnInit {
    isEditMode = false;
    form!: FormGroup;
    canEditRole = false;
    roleOptions: Array<{ value: UserRole; label: string }> = [];
    // True once the authoritative user_type id<->name map has loaded from the backend.
    // Role display/edit is gated on this — never guess a role from a raw id.
    mapsLoaded = false;
    private idToRole = new Map<number, UserRole>();
    private roleToId = new Map<UserRole, number>();
    sites: Site[] = [];
    groups: Group[] = [];
    sitesLoading = false;
    groupsLoading = false;
    countries = [
        { name: "South Africa", code: "ZA", dial_code: "+27" },
        { name: "United Kingdom", code: "GB", dial_code: "+44" },
        { name: "United States", code: "US", dial_code: "+1" },
        { name: "Namibia", code: "NA", dial_code: "+264" },
        { name: "Botswana", code: "BW", dial_code: "+267" },
        { name: "Zimbabwe", code: "ZW", dial_code: "+263" },
        { name: "Mozambique", code: "MZ", dial_code: "+258" },
        { name: "Lesotho", code: "LS", dial_code: "+266" },
        { name: "Eswatini", code: "SZ", dial_code: "+268" },
        { name: "Australia", code: "AU", dial_code: "+61" },
        { name: "New Zealand", code: "NZ", dial_code: "+64" },
        { name: "India", code: "IN", dial_code: "+91" },
        { name: "Canada", code: "CA", dial_code: "+1" },
        { name: "Germany", code: "DE", dial_code: "+49" },
        { name: "France", code: "FR", dial_code: "+33" },
        { name: "Netherlands", code: "NL", dial_code: "+31" },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { user: User },
        private dialogRef: MatDialogRef<ViewUserDialogComponent>,
        private fb: FormBuilder,
        private userService: UserService,
        private snackBar: MatSnackBar,
        private siteService: SiteService,
        private groupService: GroupService,
        private userTypeService: UserTypeService,
        public authService: AuthService
    ) {
        // canEditRole starts false (fail-closed) — it's only computed once the
        // authoritative user_type id<->name map has loaded, see loadUserTypes().
        this.roleOptions = [
            { value: "default",      label: "Candidate (Default)" },
            { value: "consultant",   label: "Consultant" },
            { value: "hr_personnel", label: "HR Personnel" },
        ];
        if (this.authService.isAdminOrManager()) {
            this.roleOptions.push({ value: "hr_manager", label: "HR Manager" });
        }
        if (this.authService.isAdmin()) {
            this.roleOptions.push({ value: "admin", label: "Admin" });
            this.roleOptions.push({ value: "super_admin", label: "Super Admin" });
        }

        this.initializeForm();
    }

    initializeForm(): void {
        let countryCode = 'ZA';
        let phoneNumber = '';

        if (this.data.user.msisdn) {
            const fullNumber = this.data.user.msisdn.toString();
            // Try to find the matching country code
            const matchedCountry = this.countries.find(c => {
                const dialCode = c.dial_code.replace('+', '');
                return fullNumber.startsWith(dialCode);
            });

            if (matchedCountry) {
                countryCode = matchedCountry.code;
                const dialCode = matchedCountry.dial_code.replace('+', '');
                phoneNumber = fullNumber.substring(dialCode.length);
            } else {
                phoneNumber = fullNumber;
            }
        }

        this.form = this.fb.group({
            name: [this.data.user.name, Validators.required],
            surname: [this.data.user.surname, Validators.required],
            email: [this.data.user.email, [Validators.email]],
            gender: [this.data.user.gender, Validators.required],
            job_description: [this.data.user.job_description],
            countryCode: [countryCode, Validators.required],
            msisdn: [
                phoneNumber,
                [Validators.required, Validators.pattern(/^\d{4,15}$/)],
            ],
            group_id: [this.data.user.group_id],
            site_id: [this.data.user.site_id],
            user_type_id: [this.data.user.user_type_id],
            // Safe (fallback-only) before loadUserTypes() resolves — patched there,
            // and correct on every subsequent call (e.g. cancelEdit's form reset).
            user_type: [this.roleFromTypeId(this.data.user.user_type_id)],
            assessment_started: [this.data.user.assessment_started],
            assessment_completed: [this.data.user.assessment_completed],
            identity_number: [
                this.data.user.identity_number,
                [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.pattern(/^\d+$/),
                ],
            ],
        });
    }

    // Loads the authoritative user_type id<->name map from the backend and only
    // then computes anything role-derived (canEditRole, the role form control,
    // display label). Row ids are never assumed — see UserTypeService for why.
    private loadUserTypes(): void {
        this.userTypeService.list().subscribe({
            next: (types) => {
                this.idToRole.clear();
                this.roleToId.clear();
                for (const t of types) {
                    this.idToRole.set(t.id, t.name as UserRole);
                    this.roleToId.set(t.name as UserRole, t.id);
                }

                const currentUser = this.authService.getCurrentUser();
                const targetIsManager = this.isManagerLevelUser(this.data.user.user_type_id);
                const callerIsHRStaff = !this.authService.isAdminOrManager() && this.authService.canAddUsers();
                const targetIsCandidate = this.isCandidate();
                this.canEditRole = this.authService.canAddUsers()
                    && currentUser?.id !== this.data.user.id
                    && !(callerIsHRStaff && targetIsManager)
                    && !(callerIsHRStaff && targetIsCandidate);

                this.form.get('user_type')?.setValue(this.roleFromTypeId(this.data.user.user_type_id), { emitEvent: false });
                this.mapsLoaded = true;
            },
            error: () => {
                // Fail closed: leave canEditRole false and mapsLoaded false so the
                // UI shows "Unknown" and hides role editing rather than guessing.
                this.snackBar.open("Failed to load role definitions — role editing disabled.", "", { duration: 4000 });
            },
        });
    }

    ngOnInit(): void {
        this.loadUserTypes();

        this.sitesLoading = true;
        this.siteService.getSites().subscribe({
            next: (sites) => { this.sites = sites; this.sitesLoading = false; },
            error: () => { this.sitesLoading = false; },
        });
        if (this.data.user.site_id) {
            this.loadGroupsForSite(this.data.user.site_id);
        }

        this.form.get('site_id')!.valueChanges.subscribe((siteId: number | null) => {
            this.form.get('group_id')!.setValue(null, { emitEvent: false });
            this.groups = [];
            if (siteId) {
                this.loadGroupsForSite(siteId);
            }
        });
    }

    private loadGroupsForSite(siteId: number): void {
        this.groupsLoading = true;
        this.groupService.getGroups(siteId).subscribe({
            next: (groups) => { this.groups = groups; this.groupsLoading = false; },
            error: () => { this.groupsLoading = false; },
        });
    }

    getSiteName(siteId: number): string {
        if (!siteId) return 'N/A';
        const site = this.sites.find(s => s.id === siteId);
        return site?.name ?? `Site ${siteId}`;
    }

    getGroupName(groupId: number): string {
        if (!groupId) return 'N/A';
        const group = this.groups.find(g => g.id === groupId);
        return group?.name ?? `Group ${groupId}`;
    }

    isManagerLevelUser(userTypeId: number | null | undefined): boolean {
        const role = this.roleFromTypeId(userTypeId);
        return role === 'hr_manager' || role === 'admin' || role === 'super_admin';
    }

    isCandidate(): boolean {
        return this.roleFromTypeId(this.data.user.user_type_id) === 'default';
    }

    openInviteForUser(): void {
        this.dialogRef.close({ action: 'invite', email: this.data.user.email });
    }

    resendingSetup = false;

    resendAccountSetup(): void {
        this.resendingSetup = true;
        this.userService.resendAccountSetup(this.data.user.id).subscribe({
            next: (res) => {
                this.resendingSetup = false;
                this.snackBar.open(res.message || 'Account setup email sent.', 'Close', {
                    duration: 4000,
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar'],
                });
            },
            error: (err) => {
                this.resendingSetup = false;
                const msg = err.error?.error || err.error?.message || 'Failed to send account setup email.';
                this.snackBar.open(msg, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
            },
        });
    }

    enableEditMode(): void {
        this.isEditMode = true;
    }

    cancelEdit(): void {
        this.isEditMode = false;
        this.initializeForm(); // Reset form to original values
    }

    formatPhoneNumber(msisdn: number | undefined): string {
        if (!msisdn) return 'N/A';
        const numberStr = msisdn.toString();
        const matchedCountry = this.countries.find(c => {
            const dialCode = c.dial_code.replace('+', '');
            return numberStr.startsWith(dialCode);
        });
        if (matchedCountry) {
            const dialCode = matchedCountry.dial_code.replace('+', '');
            const phoneNumber = numberStr.substring(dialCode.length);
            return `${matchedCountry.dial_code} ${phoneNumber}`;
        }
        return numberStr;
    }

    onNumberKeyPress(event: KeyboardEvent): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        // Allow: backspace, delete, tab, escape, enter, and numbers (0-9)
        if (
            charCode === 8 || // backspace
            charCode === 9 || // tab
            charCode === 27 || // escape
            charCode === 13 || // enter
            charCode === 46 || // delete
            (charCode >= 35 && charCode <= 40) // home, end, left, right, up, down
        ) {
            return true;
        }
        // Ensure that it is a number and stop the keypress
        if ((charCode >= 48 && charCode <= 57)) {
            return true;
        }
        event.preventDefault();
        return false;
    }

    onNumberInput(event: any, fieldName: string): void {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/[^0-9]/g, '');
        this.form.get(fieldName)?.setValue(value, { emitEvent: false });
    }

    // Resolves via the id<->name map loaded from the backend (see loadUserTypes()).
    // Falls back to the lowest-privilege role if the map hasn't loaded yet or the
    // id is unrecognised — never guess a higher-privilege role from a raw id.
    private roleFromTypeId(userTypeId: number | null | undefined): UserRole {
        if (userTypeId == null) return "default";
        return this.idToRole.get(Number(userTypeId)) ?? "default";
    }

    private typeIdFromRole(role: UserRole): number | null {
        return this.roleToId.get(role) ?? null;
    }

    getRoleLabel(userTypeId: number | null | undefined): string {
        const labelMap: Record<string, string> = {
            super_admin: 'Super Admin',
            admin: 'Admin',
            hr_manager: 'HR Manager',
            hr_personnel: 'HR Personnel',
            consultant: 'Consultant',
            default: 'Candidate',
        };
        const role = this.roleFromTypeId(userTypeId);
        return labelMap[role] ?? role;
    }

    onSubmit(): void {
        if (this.form.valid) {
            const formValue: any = { ...this.form.value };

            if (!formValue.email) {
                delete formValue.email;
            }

            // Format MSISDN
            const selectedCountry = this.countries.find(c => c.code === formValue.countryCode);
            const dialCode = selectedCountry ? selectedCountry.dial_code.replace('+', '') : '27';
            let phoneNumber = formValue.msisdn.toString().trim();

            // Remove leading zero if present
            if (phoneNumber.startsWith('0')) {
                phoneNumber = phoneNumber.substring(1);
            }

            const fullMsisdn = `${dialCode}${phoneNumber}`;

            // Update form value
            formValue.msisdn = Number(fullMsisdn);
            delete formValue.countryCode;

            const selectedRole = (formValue.user_type as UserRole) ?? this.roleFromTypeId(this.data.user.user_type_id);
            const currentRole = this.roleFromTypeId(this.data.user.user_type_id);
            const roleChanged = this.canEditRole && selectedRole !== currentRole;

            delete formValue.user_type;

            const update$ = roleChanged
                ? this.userService.updateUserRole(this.data.user.id, { user_type: selectedRole }).pipe(
                      switchMap((roleRes) => {
                          // If the target was a candidate, the backend defers the role
                          // change to an invite instead of applying it immediately —
                          // don't overwrite the local/displayed role in that case.
                          if (!roleRes.invite_sent) {
                              const newTypeId = this.typeIdFromRole(selectedRole);
                              if (newTypeId != null) {
                                  this.data.user.user_type_id = newTypeId;
                                  this.form.get("user_type_id")?.setValue(newTypeId, { emitEvent: false });
                                  formValue.user_type_id = newTypeId;
                              }
                          }
                          return this.userService.updateUser(this.data.user.id, formValue).pipe(
                              map(() => roleRes)
                          );
                      })
                  )
                : this.userService.updateUser(this.data.user.id, formValue).pipe(
                      map(() => null as UpdateUserRoleResponse | null)
                  );

            update$.subscribe({
                next: (roleRes) => {
                    const message = roleRes?.invite_sent
                        ? (roleRes.message || "Invite sent — the user's role will update once they set a password.")
                        : "User updated successfully";
                    this.snackBar.open(message, "Close", {
                        duration: roleRes?.invite_sent ? 6000 : 3000,
                        verticalPosition: "top",
                        panelClass: ["success-snackbar"],
                    });
                    this.isEditMode = false;
                    Object.assign(this.data.user, formValue);
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    const msg = err.error?.error || err.error?.message || err.message || "Failed to update user. Please try again.";
                    this.snackBar.open(msg, "Close", {
                        duration: 5000,
                        panelClass: ["error-snackbar"],
                    });
                },
            });
        }
    }
}
